import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { countryCodes } from '../data/mockData';

const HeroBanner = () => {
  const { toast } = useToast();
  const hero = useScrollAnimation({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    setErrors({ ...errors, email: value && !validateEmail(value) ? 'Enter a valid email address' : '' });
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      setFormData({ ...formData, phone: digitsOnly });
      setErrors({ ...errors, phone: digitsOnly && digitsOnly.length !== 10 ? 'Must be exactly 10 digits' : '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }

    toast({ title: "We Got Your Request!", description: "A trademark specialist will call you within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" data-testid="hero-section" ref={hero.ref} className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden bg-white">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1713475922974-25cd0ece56b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGdsYXNzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc2MTk4MDI5fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/95 to-[#F9FAFB]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 w-full">
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className={`space-y-6 scroll-slide-left ${hero.isVisible ? 'scroll-visible' : ''}`}>
            <div className="inline-block bg-[#FF1010] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
              Don't Wait — Protect Your Brand Now
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#111827] leading-[1.05] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your Brand Is
              <span className="block text-[#FF1010]">Not Protected.</span>
              <span className="block">Fix That Today.</span>
            </h1>

            <p className="text-base text-[#4B5563] leading-relaxed max-w-md">
              Every day without a registered trademark is a day your brand is exposed to copycats, competitors, and counterfeiters. Trademark Agency USA files with the USPTO and gets it done — starting at $45.
            </p>

            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                '50-state federal protection',
                'Licensed attorneys on staff',
                'Comprehensive trademark search',
                'Flat-rate from $45'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#FF1010] flex-shrink-0" strokeWidth={3} />
                  <span className="text-sm font-medium text-[#111827]">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-6 pt-4">
              <div className="text-center">
                <span className="text-3xl font-black text-[#111827]" style={{ fontFamily: "'Outfit', sans-serif" }}>10K+</span>
                <span className="block text-xs text-[#4B5563] font-medium uppercase tracking-wider">Filed</span>
              </div>
              <div className="w-px h-10 bg-[#E5E7EB]" />
              <div className="text-center">
                <span className="text-3xl font-black text-[#111827]" style={{ fontFamily: "'Outfit', sans-serif" }}>98%</span>
                <span className="block text-xs text-[#4B5563] font-medium uppercase tracking-wider">Approved</span>
              </div>
              <div className="w-px h-10 bg-[#E5E7EB]" />
              <div className="text-center">
                <span className="text-3xl font-black text-[#111827]" style={{ fontFamily: "'Outfit', sans-serif" }}>24hr</span>
                <span className="block text-xs text-[#4B5563] font-medium uppercase tracking-wider">Response</span>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className={`scroll-slide-right scroll-delay-2 ${hero.isVisible ? 'scroll-visible' : ''}`}>
            <div data-testid="hero-contact-form" className="bg-white border-2 border-[#111827] p-8 shadow-[8px_8px_0px_#FF1010]">
              <div className="mb-6">
                <h3 className="text-2xl font-black tracking-tighter text-[#111827] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Start Your Registration
                </h3>
                <p className="text-sm text-[#4B5563] mt-1">
                  Fill this out. We call you within 24 hours. Free.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="hero-name" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Full Name *</Label>
                  <Input id="hero-name" data-testid="hero-name-input" placeholder="Your name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-11 bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#111827] focus:ring-1 focus:ring-[#111827] rounded-none mt-1" />
                </div>

                <div>
                  <Label htmlFor="hero-email" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Email *</Label>
                  <Input id="hero-email" data-testid="hero-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleEmailChange(e.target.value)} required className={`h-11 bg-[#F9FAFB] text-[#111827] placeholder:text-[#9CA3AF] rounded-none mt-1 ${errors.email ? 'border-[#FF1010]' : 'border-[#E5E7EB]'} focus:border-[#111827] focus:ring-1 focus:ring-[#111827]`} />
                  {errors.email && <p className="text-[#FF1010] text-xs mt-0.5 font-medium">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="hero-phone" className="text-[#111827] text-xs font-bold uppercase tracking-wider">Phone *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                      <SelectTrigger data-testid="hero-country-select" className="w-[100px] h-11 bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] focus:border-[#111827] rounded-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px] bg-white border-[#E5E7EB]">
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code} className="text-[#111827]">
                            <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input id="hero-phone" data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-11 bg-[#F9FAFB] text-[#111827] placeholder:text-[#9CA3AF] rounded-none ${errors.phone ? 'border-[#FF1010]' : 'border-[#E5E7EB]'} focus:border-[#111827] focus:ring-1 focus:ring-[#111827]`} />
                  </div>
                  {errors.phone && <p className="text-[#FF1010] text-xs mt-0.5 font-medium">{errors.phone}</p>}
                </div>

                <div>
                  <Label htmlFor="hero-desc" className="text-[#111827] text-xs font-bold uppercase tracking-wider">What Do You Need?</Label>
                  <Textarea id="hero-desc" data-testid="hero-description-input" placeholder="e.g. Register my brand name and logo with the USPTO" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-[#F9FAFB] border-[#E5E7EB] text-[#111827] placeholder:text-[#9CA3AF] focus:border-[#111827] focus:ring-1 focus:ring-[#111827] resize-none rounded-none mt-1" />
                </div>

                <Button type="submit" data-testid="hero-contact-submit" className="w-full bg-[#FF1010] hover:bg-[#CC0000] text-white h-12 text-sm font-black rounded-none uppercase tracking-widest btn-brutal">
                  Get My Free Consultation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                <p className="text-[10px] text-[#9CA3AF] text-center">
                  By submitting, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
